import { useCallback, useEffect, useRef } from 'react';
import { notificationSound } from '../sounds/notification';

/**
 * Hook to manage notification sound effects
 * @returns {Object} Sound utility functions
 */
const useNotificationSound = () => {
  const audioRef = useRef(null);
  const lastPlayTimeRef = useRef(0);

  // Initialize audio on mount
  useEffect(() => {
    // Create audio element
    audioRef.current = new Audio(notificationSound);
    audioRef.current.volume = 0.5;
    audioRef.current.preload = 'auto';

    // Add a dummy user interaction event listener to unlock audio on iOS
    const unlockAudio = () => {
      // Create a silent audio context
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const buffer = audioContext.createBuffer(1, 1, 22050);
      const source = audioContext.createBufferSource();
      source.buffer = buffer;
      source.connect(audioContext.destination);
      source.start(0);
      
      // Play and immediately pause the audio to unlock it
      const silentPlay = () => {
        audioRef.current.play().catch(() => {});
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      };
      
      silentPlay();
      
      // Remove listeners once unlocked
      document.removeEventListener('click', unlockAudio);
      document.removeEventListener('touchstart', unlockAudio);
    };
    
    // Add listeners to unlock audio
    document.addEventListener('click', unlockAudio);
    document.addEventListener('touchstart', unlockAudio);

    // Cleanup
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      document.removeEventListener('click', unlockAudio);
      document.removeEventListener('touchstart', unlockAudio);
    };
  }, []);

  /**
   * Play notification sound
   */
  const playSound = useCallback(() => {
    // Throttle sound to prevent rapid repeats (max once per 2 seconds)
    const now = Date.now();
    if (now - lastPlayTimeRef.current < 2000) {
      console.log('Sound throttled, too soon since last play');
      return;
    }
    
    lastPlayTimeRef.current = now;
    
    if (audioRef.current) {
      // Reset audio to beginning
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      
      // Play with multiple fallbacks
      const playPromise = audioRef.current.play();
      
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          console.warn('Audio playback was prevented:', error);
          
          // Try again with user interaction check
          const resumeAudio = () => {
            if (audioRef.current) {
              audioRef.current.play().catch(e => 
                console.error('Still could not play audio:', e)
              );
            }
            document.removeEventListener('click', resumeAudio);
          };
          
          document.addEventListener('click', resumeAudio, { once: true });
        });
      }
    }
  }, []);

  /**
   * Set volume level (0-1)
   * @param {number} level - Volume level from 0 to 1
   */
  const setVolume = useCallback((level) => {
    if (audioRef.current) {
      audioRef.current.volume = Math.min(Math.max(level, 0), 1);
    }
  }, []);

  return {
    playSound,
    setVolume
  };
};

export default useNotificationSound; 